import React, { useState } from "react";
import { Trophy, ShieldCheck, Handshake, Check } from "lucide-react";
import { GAME_ITEMS, SCENARIOS } from "../courseData";

export const MatchGame = () => {
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState([]);
  const [wrong, setWrong] = useState(null);
  const [matches] = useState(() => GAME_ITEMS.map((g) => g.match).sort(() => Math.random() - 0.5));
  const pickMatch = (match) => {
    if (!selected) return;
    const correct = GAME_ITEMS.find((g) => g.term === selected)?.match === match;
    if (correct) { setMatched((m) => [...m, selected]); setSelected(null); setWrong(null); }
    else { setWrong(match); }
  };
  const done = matched.length === GAME_ITEMS.length;
  return (
    <div className="game" data-testid="match-game">
      <h3><Trophy size={18} /> Warm-up: Match the Equipment to its Freight</h3>
      {done ? (
        <div className="game-win" data-testid="match-game-win">You matched them all — you're ready to source loads.</div>
      ) : (
        <div className="game-grid">
          <div className="game-col">
            {GAME_ITEMS.map((g) => (
              <button key={g.term} disabled={matched.includes(g.term)}
                className={`game-chip ${selected === g.term ? "sel" : ""} ${matched.includes(g.term) ? "done" : ""}`}
                onClick={() => { setSelected(g.term); setWrong(null); }} data-testid={`match-term-${g.term}`}>{g.term}</button>
            ))}
          </div>
          <div className="game-col">
            {matches.map((m) => {
              const isMatched = GAME_ITEMS.some((g) => g.match === m && matched.includes(g.term));
              return (
                <button key={m} disabled={isMatched}
                  className={`game-chip ${wrong === m ? "wrong" : ""} ${isMatched ? "done" : ""}`}
                  onClick={() => pickMatch(m)}>{m}</button>
              );
            })}
          </div>
        </div>
      )}
      <p className="game-progress">{matched.length} / {GAME_ITEMS.length} matched</p>
    </div>
  );
};

export const ControlRoom = () => {
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const s = SCENARIOS[i];
  const finished = i >= SCENARIOS.length;
  const choose = (opt, idx) => { if (picked !== null) return; setPicked(idx); if (opt.correct) setScore((v) => v + 1); };
  const next = () => { setPicked(null); setI((v) => v + 1); };
  const restart = () => { setI(0); setPicked(null); setScore(0); };
  return (
    <div className="game control-room" data-testid="control-room-game">
      <h3><ShieldCheck size={18} /> Dispatch Control Room Challenge</h3>
      {finished ? (
        <div className="game-win" data-testid="control-room-result">
          You scored {score} / {SCENARIOS.length} on safe, compliant decisions.
          <button className="btn btn-primary cr-restart" onClick={restart} data-testid="control-room-restart">Play again</button>
        </div>
      ) : (
        <>
          <div className="cr-round">Round {i + 1} · {s.round}</div>
          <p className="cr-situation">{s.situation}</p>
          <div className="cr-opts">
            {s.options.map((o, idx) => {
              const state = picked === null ? "" : o.correct ? "right" : picked === idx ? "wrong" : "";
              return (<button key={idx} className={`game-chip ${state}`} onClick={() => choose(o, idx)} data-testid={`cr-opt-${idx}`}>{o.label}</button>);
            })}
          </div>
          {picked !== null && (
            <div className="cr-explain" data-testid="cr-explain">
              {s.explain}
              <button className="btn btn-primary cr-next" onClick={next} data-testid="cr-next">{i === SCENARIOS.length - 1 ? "See results" : "Next scenario"}</button>
            </div>
          )}
          <p className="game-progress">Score {score} / {SCENARIOS.length}</p>
        </>
      )}
    </div>
  );
};

export const NegotiationGame = () => {
  const FLOOR = 850, TARGET = 950, BROKER_MAX = 1050;
  const [offer, setOffer] = useState(700);
  const [attempts, setAttempts] = useState(0);
  const [log, setLog] = useState([{ who: "broker", text: `I've got a load — I can pay $700 on this lane.` }]);
  const [result, setResult] = useState(null);
  const push = (who, text) => setLog((l) => [...l, { who, text }]);
  const counter = (amount) => {
    if (result) return;
    push("you", `Let's do $${amount}.`);
    if (amount <= BROKER_MAX) {
      push("broker", `Deal at $${amount}. Send the driver info.`);
      const profit = amount - FLOOR;
      setResult({ win: true, rate: amount, msg: amount >= TARGET ? `Excellent — you beat your target! Carrier profit over floor: $${profit}.` : `Booked above your floor. Carrier profit: $${profit}. A bit under target, but solid.` });
      return;
    }
    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);
    if (nextAttempts >= 3) { push("broker", `That's too high. My final is $${BROKER_MAX} — take it or leave it.`); setOffer(BROKER_MAX); }
    else { const mid = Math.round((offer + BROKER_MAX) / 2); push("broker", `Can't hit that. I can come up to $${mid}.`); setOffer(mid); }
  };
  const accept = () => {
    if (result) return;
    push("you", `I'll take $${offer}.`);
    const profit = offer - FLOOR;
    setResult({ win: offer >= FLOOR, rate: offer, msg: offer >= TARGET ? `Great close at $${offer}!` : offer >= FLOOR ? `Accepted at $${offer}. Profit over floor: $${profit}.` : `You booked below your cost floor — that load loses money.` });
  };
  const walk = () => { if (result) return; push("you", `That doesn't work for my carrier. I'll pass.`); setResult({ win: null, rate: 0, msg: `You walked away. Sometimes 'no' protects your carrier — but you booked $0 today.` }); };
  const restart = () => { setOffer(700); setAttempts(0); setResult(null); setLog([{ who: "broker", text: `I've got a load — I can pay $700 on this lane.` }]); };
  const counters = [900, 1000, 1100].filter((c) => c > offer);
  return (
    <div className="game nego" data-testid="negotiation-game">
      <h3><Handshake size={18} /> Rate Negotiation Trainer</h3>
      <p className="sim-intro">Your cost floor is <strong>${FLOOR}</strong>, target <strong>${TARGET}</strong>. Negotiate the broker up without losing the load.</p>
      <div className="nego-chat" data-testid="nego-chat">
        {log.map((m, i) => (<div key={i} className={`nego-msg ${m.who}`}><span className="nego-who">{m.who === "you" ? "You" : "Broker"}</span>{m.text}</div>))}
      </div>
      {result ? (
        <div className={`nego-result ${result.win ? "ok" : "no"}`} data-testid="nego-result">
          <p>{result.msg}</p>
          <button className="btn btn-primary" onClick={restart} data-testid="nego-restart">Try again</button>
        </div>
      ) : (
        <div className="nego-actions">
          <button className="game-chip" onClick={accept} data-testid="nego-accept">Accept ${offer}</button>
          {counters.map((c) => (<button key={c} className="game-chip" onClick={() => counter(c)} data-testid={`nego-counter-${c}`}>Counter ${c}</button>))}
          <button className="game-chip walkbtn" onClick={walk} data-testid="nego-walk">Walk away</button>
        </div>
      )}
    </div>
  );
};
